import * as React from "react";
import { useCallback, useEffect, useState } from "react";
import styled from "@mui/material/styles/styled";
import Dialog from "@mui/material/Dialog/Dialog";
import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Theme,
} from "@mui/material";
import { RefineFeaturePanel } from "./refineFeaturePanel";
import { featureTo2Lines } from "fe-common/logic";
import { Qualifier } from "fe-common/graphql";
import {
  PatientCaseChange,
  PatientCaseOp,
  usePatientCase,
} from "../usePatientCase";
import { useDiagnosticNavigation } from "../useDiagnosticNavigation";
import {
  PatientCaseGrouping,
  RefinePopupContextProps,
} from "../../../context/diagnosticAgent";
import { DataInput } from "../../dataInputTypes";
import { makeStyles } from "@mui/styles";
import { MobileView } from "../../../util/aKaunGeneralUtil";
import { ACTION, CATEGORY, evt } from "fe-common/analytics/analytics";
import { KahunColorMap } from "fe-common/ui/assets/colorMap";
import IconButton from "@mui/material/IconButton";
import CancelIcon from "@mui/icons-material/Cancel";
//region [[ Styles ]]

const RefinePopupView = styled("div")(({ theme }) => ({}));

const ActionsStyled = styled(DialogActions)(({ theme }) => ({
  display: "flex",
  flexDirection: "row-reverse",
  justifyContent: "space-between",
  alignItems: "center",
  marginTop: "5px",
  marginBottom: "5px",
  paddingLeft: theme.spacing(4),
  paddingRight: theme.spacing(4),
  boxShadow: "0px 0px 17px rgba(0, 0, 0, 0.08)",
  [theme.breakpoints.down(MobileView)]: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },
}));

const ActionButton = styled(Button)(({ theme }) => ({
  textTransform: "capitalize",
  boxShadow: "none",
  fontSize: "16px",

  width: "158px",

  borderRadius: "25px",
  backgroundColor: KahunColorMap.Primary,
  "&:hover": {
    backgroundColor: KahunColorMap.Primary,
    boxShadow: "none",
  },
  color: "white",

  [theme.breakpoints.down(MobileView)]: {
    width: "110px",
  },
}));

const CloseButton = styled(IconButton)(({ theme }) => ({
  position: "absolute",
  right: "25px",
  top: "calc(50% - 25px)",
  [theme.breakpoints.down(MobileView)]: {
    right: "5px",
  },
}));

const CloseIcon = styled(CancelIcon)(({ theme }) => ({
  color: "white",
  fontSize: "24px",
}));

const SecondaryButton = styled(Button)(({ theme }) => ({
  textTransform: "capitalize",
  boxShadow: "none",
  fontSize: "16px",
  width: "158px",
  borderColor: KahunColorMap.Primary,
  backgroundColor: "white",
  color: KahunColorMap.Primary,
  borderRadius: "25px",
  "&:hover": {
    borderColor: KahunColorMap.Primary,
    backgroundColor: "white",
    color: KahunColorMap.Primary,
  },

  [theme.breakpoints.down(MobileView)]: {
    width: "110px",
  },
}));

const RefinePanelContainer = styled("div")(({ theme }: { theme?: Theme }) => ({
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  paddingTop: theme.spacing(2),
  paddingBottom: theme.spacing(3),

  [theme.breakpoints.down(MobileView)]: {
    paddingLeft: theme.spacing(0),
    paddingRight: theme.spacing(0),
  },
}));

const RefineTitle = styled(DialogTitle)(({ theme }) => ({
  color: "white",
  fontSize: "22px",
  fontWeight: 700,

  [theme.breakpoints.down(MobileView)]: {
    lineHeight: 1.2,
    maxWidth: "190px",
    paddingRight: "0px",
  },
}));

const HeaderContainer = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  minHeight: "60px",
  paddingLeft: theme.spacing(1),
  paddingRight: theme.spacing(4),
  backgroundColor: KahunColorMap.PrimaryDim,
  boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.08)",
  position: "relative",

  [theme.breakpoints.down(MobileView)]: {
    paddingLeft: theme.spacing(0),
    paddingRight: theme.spacing(2),
  },
}));

const useStyles = makeStyles((theme?: Theme) => ({
  dialogPaper: {
    margin: "15px",

    [theme.breakpoints.up(MobileView)]: {
      height: "600px",
      minWidth: "600px",
    },
    [theme.breakpoints.down(MobileView)]: {
      height: "unset",
      minWidth: "unset",
    },

    borderRadius: "14px",
    boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.08)",
  },
}));

//endregion [[ Styles ]]

//region [[ Props ]]

interface RefinePopupViewProps {
  className?: string;
  context?: RefinePopupContextProps;
  onDone?: (e, change: PatientCaseChange) => void;
  updatePatientCase?: boolean;
  showDelete?: boolean;
  showYesNo?: boolean;
  showDuration?: boolean;
}

//endregion [[ Props ]]

//region [[ Functions ]]
//endregion [[ Functions ]]

export const RefinePopup = ({
  context,
  showYesNo = true,
  showDelete = true,
  updatePatientCase = true,
  showDuration = true,
  ...props
}: RefinePopupViewProps) => {
  const { setRefinePopupProps, refinePopupProps } = useDiagnosticNavigation();

  const {
    open,
    refineQualifier,
    patientCaseGrouping,
    causedByDisease,
  } = React.useMemo(
    () => context || refinePopupProps || ({} as RefinePopupContextProps),
    [context, refinePopupProps],
  );
  const classes = useStyles();

  const [refineQf, setRefineQf] = useState(
    refineQualifier ? refineQualifier : null,
  );
  const [change, setChange] = useState(null);

  useEffect(() => {
    if (refineQualifier) {
      setRefineQf(refineQualifier);
    }
  }, [refineQualifier]);

  useEffect(() => {
    if (open) {
      setChange(null);
    }
  }, [open]);

  const { doChangesToPatientCase, handleChipRemove } = usePatientCase();

  const onChange = useCallback(
    (qualifiers: Qualifier[]) => {
      setRefineQf({
        ...refineQf,
        qualifiers: qualifiers,
      });

      setChange({
        op: PatientCaseOp.replaceQFInSection,
        qf: {
          ...refineQf,
          qualifiers,
        },
        section: patientCaseGrouping || PatientCaseGrouping.CLINICAL_FEATURES,
      });
    },
    [patientCaseGrouping, refineQf],
  );

  const onResponse = useCallback(
    (response: DataInput) => {
      evt({
        category: CATEGORY.REFINE_POPUP,
        action:
          response.negate === true
            ? ACTION.NEGATE_FINDING
            : ACTION.MAKE_FINDING_POSITIVE,
        label: refineQf.name,
      });

      setRefineQf({
        ...refineQf,
        negate: response.negate,
      });

      setChange({
        op: PatientCaseOp.replaceQFInSection,
        qf: {
          ...refineQf,
          negate: response.negate,
          qualifiers: [],
        },
        section: patientCaseGrouping || PatientCaseGrouping.CLINICAL_FEATURES,
      });
    },
    [patientCaseGrouping, refineQf],
  );

  const handleCancel = useCallback(
    (event) => {
      if (props.onDone) {
        setChange(null);
        props.onDone(event, null);
      }
      if (!context) {
        setRefinePopupProps({ open: false });
      }
    },
    [setRefinePopupProps, context, props],
  );

  const onDelete = useCallback(
    (event) => {
      evt({
        category: CATEGORY.REFINE_POPUP,
        action: ACTION.REFINE_POPUP_DELETE,
        label: refineQf.name,
      });

      handleChipRemove(refineQf, patientCaseGrouping);
      handleCancel(event);
    },
    [handleCancel, refineQf, handleChipRemove, patientCaseGrouping],
  );

  const handleDone = (e) => {
    evt({
      category: CATEGORY.REFINE_POPUP,
      action: ACTION.REFINE_POPUP_DONE,
      label: refineQf.name,
    });

    if (updatePatientCase && change) {
      doChangesToPatientCase([change]);
    }

    if (props.onDone) props.onDone(e, change);

    if (!context) setRefinePopupProps({ open: false });
  };

  const feature2Lines = refineQf && featureTo2Lines(refineQf);
  const refineQfText = feature2Lines ? feature2Lines[0] : "";

  return (
    <RefinePopupView>
      {refineQf && (
        <Dialog
          open={open}
          onClose={handleCancel}
          aria-labelledby="form-dialog-title"
          classes={{ paper: classes.dialogPaper }}
        >
          <HeaderContainer>
            <RefineTitle>{`${refineQfText}`}</RefineTitle>
            <CloseButton onClick={handleCancel}>
              <CloseIcon />
            </CloseButton>
          </HeaderContainer>
          <DialogContent>
            <RefinePanelContainer>
              <RefineFeaturePanel
                onChangeNegate={causedByDisease ? null : onResponse}
                showTitle={false}
                onChange={onChange}
                selectedQualifiers={refineQf?.qualifiers}
                qf={refineQf}
                showDuration={showDuration}
                causedByDisease={causedByDisease}
              />
            </RefinePanelContainer>
          </DialogContent>
          <ActionsStyled>
            <ActionButton
              onClick={handleDone}
              color={change ? "primary" : "inherit"}
              variant={"contained"}
            >
              Done
            </ActionButton>
            {showDelete && (
              <SecondaryButton onClick={onDelete} variant={"outlined"}>
                Delete
              </SecondaryButton>
            )}
          </ActionsStyled>
        </Dialog>
      )}
    </RefinePopupView>
  );
};
